import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Table,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  TableBody,
  TablePagination,
  Paper,
  IconButton,
  Modal,
  TextField,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  fetchAcademicSessions,
  selectAllSessions,
  selectSessionsLoading,
  selectSessionsError,
} from "../../../../Slices/sessionsSlice";
import {
  getFeesHistory,
  payFees,
  downloadFeesReceipt,
  requestPayment,
} from "../../../../ApiService/ApiService";
import {
  Autorenew,
  Paid,
  Payment,
  AdminPanelSettings,
  MonetizationOn,
  Download,
  CheckCircle,
  Dangerous,
  ChevronRight,
  ArrowBack,
  Cancel,
  Check,
} from "@mui/icons-material";
import { CalendarIcon } from "@mui/x-date-pickers";
import { formatDateOutput } from "../../../../Utils/formatDate";
import CustomDialog from "../../../../Components/CustomDialog";

function FeesHistory({ setActionComponent, selectedStudentId, studentName }) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchAcademicSessions());
  }, [dispatch]);

  const sessions = useSelector(selectAllSessions);
  const sessionsLoading = useSelector(selectSessionsLoading);
  const sessionsError = useSelector(selectSessionsError);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [paginatedData, setPaginatedData] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(1);
  const [feesHistoryDetails, setFeesHistoryDetails] = useState({});
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isPartial, setIsPartial] = useState(false);
  const [amountToPay, setAmountToPay] = useState("");
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const currentDate = new Date().toISOString().slice(0, 10);

  const [selectedPayment, setSelectedPayment] = useState({
    studentId: "",
    grade: "",
    feesName: "",
    amount: "",
    startDate: "",
    endDate: "",
    paidOn: "",
    paymentMode: "",
  });

  useEffect(() => {
    setPaginatedData(feesHistoryDetails?.data);
  }, [feesHistoryDetails]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const fetchFeesHistory = async () => {
    try {
      setLoading(true);
      const response = await getFeesHistory(
        selectedStudentId,
        selectedSessionId
      );
      setFeesHistoryDetails(response?.data);
    } catch (error) {
      console.log("Error fetching fees history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeesHistory(selectedStudentId, selectedSessionId);
  }, [selectedStudentId, selectedSessionId]);

  const handleSessionChange = async (event) => {
    setSelectedSessionId(event.target.value);
  };

  const filterData = (data) => {
    switch (filter) {
      case "Paid":
        return data.filter((item) => !item.isDue);
      case "Unpaid":
        return data.filter((item) => item.isDue);
      default:
        return data;
    }
  };

  const filteredData = filterData(paginatedData);

  const slicedData = filteredData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  console.log("FEES DATA", filteredData);

  const handlePayNowClick = (
    studentId,
    historyFeeId,
    grade,
    feesName,
    amount,
    startDate,
    endDate,
    paidOn,
    paymentMode
  ) => {
    setSelectedPayment({
      studentId,
      historyFeeId,
      grade,
      feesName,
      amount,
      startDate,
      endDate,
      paidOn,
      paymentMode,
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPayment({
      studentId: "",
      historyFeeId: "",
      grade: "",
      feesName: "",
      amount: "",
      startDate: "",
      endDate: "",
      paidOn: "",
      paymentMode: "",
    });
  };

  const handlePaymentDateChange = (event) => {
    const paidOn = event.target.value;
    setSelectedPayment((prevPayment) => ({
      ...prevPayment,
      paidOn,
    }));
  };

  const handlePaymentModeChange = (event) => {
    const paymentMode = event.target.value;
    setSelectedPayment((prevPayment) => ({
      ...prevPayment,
      paymentMode,
    }));
  };

  const handleConfirmPayment = async () => {
    // console.log("PAY", selectedPayment);

    if (amountToPay > selectedPayment?.amount) {
      setAmountToPay("");
      setDialog({
        visible: true,
        title: "Error",
        message: "Fees amount exceeded",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    } else {
      const payData = {
        studentId: selectedPayment?.studentId,
        historyFeeId: selectedPayment?.historyFeeId,
        paidOn: selectedPayment?.paidOn,
        paymentMode: selectedPayment?.paymentMode,
        isPartial: isPartial,
        amount: isPartial ? parseFloat(amountToPay) : selectedPayment?.amount,
      };
      console.log(payData);
      try {
        const response = await payFees(payData);
        // console.log("RES", response);
        if (response) {
          if (response?.data?.status === 200) {
            setDialog({
              visible: true,
              title: "Success",
              message: "Payment completed",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
            fetchFeesHistory(selectedStudentId, selectedSessionId);
            setSelectedPayment({
              studentId: "",
              historyFeeId: "",
              grade: "",
              feesName: "",
              amount: "",
              startDate: "",
              endDate: "",
              paidOn: currentDate,
              paymentMode: "Cash",
            });
            setIsPartial(false);
            setAmountToPay("");
            handleCloseModal();
          } else {
            const errorMessage =
              response?.data?.errorMessage || "Something went wrong";
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } catch (error) {
        setDialog({
          visible: true,
          title: "Error",
          message: "Something went wrong",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    }
  };

  const handleRequestPayment = async (historyFeeId) => {
    try {
      const response = await requestPayment(historyFeeId);
      console.log("RES", response?.data);
      if (response) {
        if (response?.data?.status === 200) {
          setDialog({
            visible: true,
            title: "Success",
            message: "Email sent to registered email ID",
            buttons: [{ name: "OK", onPress: handleDialogClose }],
          });
        } else {
          const errorMessage =
            response?.data?.errorMessage || "Something went wrong";
          setDialog({
            visible: true,
            title: "Error",
            message: errorMessage,
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Error",
          message: "Invalid response from the server",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const handleDownloadReceipt = async (historyFeeId) => {
    try {
      const response = await downloadFeesReceipt(historyFeeId);
      if (response) {
        if (response?.data?.status === 200) {
          const pdfUrl = response?.data?.data;
          if (typeof pdfUrl === "string" && pdfUrl.trim() !== "") {
            window.open(pdfUrl, "_blank");
            setDialog({
              visible: true,
              title: "Success",
              message: "Receipt downloaded",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
          } else {
            setDialog({
              visible: true,
              title: "Error",
              message: "Failed to donwload receipt",
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          const errorMessage =
            response?.data?.errorMessage || "Something went wrong";
          setDialog({
            visible: true,
            title: "Error",
            message: errorMessage,
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Error",
          message: "Invalid response from the server",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const headCells = [
    {
      id: "feeType",
      label: "Fee Type",
      icon: <Payment sx={{ marginRight: 1 }} />,
    },
    {
      id: "fromDate",
      label: "From Date",
      icon: <CalendarIcon sx={{ marginRight: 1 }} />,
    },
    {
      id: "toDate",
      label: "To Date",
      icon: <CalendarIcon sx={{ marginRight: 1 }} />,
    },
    {
      id: "totalamount",
      label: "Total Amount",
      icon: <MonetizationOn sx={{ marginRight: 1 }} />,
    },
    {
      id: "amountPaid",
      label: "Amount Paid",
      icon: <MonetizationOn sx={{ marginRight: 1 }} />,
    },
    {
      id: "paidOn",
      label: "Paid On",
      icon: <Paid sx={{ marginRight: 1 }} />,
    },
    {
      id: "isDue",
      label: "Is Due",
      icon: <Autorenew sx={{ marginRight: 1 }} />,
    },
    {
      id: "actions",
      label: "Actions",
      icon: <AdminPanelSettings sx={{ marginRight: 1 }} />,
    },
  ];

  // console.log("Fees: ", feesHistoryDetails);

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            paragraph
            sx={{
              color: "black",
              fontWeight: 600,
              fontSize: 22,
            }}
            xs={4}
            sm={4}
            md={6}
            lg={8}
            xl={8}
          >
            Fees History
          </Typography>
          <Button
            variant="contained"
            color="inherit"
            size="small"
            sx={{ textTransform: "unset", borderRadius: "10px", width: "10%" }}
            onClick={() => setActionComponent(null)}
          >
            <ArrowBack fontSize="small" sx={{ marginRight: 1 }} />
            Back
          </Button>
        </Box>

        <FormControl
          size="small"
          sx={{ marginTop: 2, marginBottom: 2 }}
          fullWidth
        >
          <InputLabel id="class">Sessions</InputLabel>
          <Select
            name="sessionId"
            label="Sessions"
            labelId="sessions"
            value={selectedSessionId}
            onChange={handleSessionChange}
            size="small"
            disabled={sessionsLoading || sessionsError}
            sx={{ width: "25%" }}
          >
            {sessions?.data?.length > 0 ? (
              sessions?.data?.map((currSession) => (
                <MenuItem key={currSession.id} value={currSession.id}>
                  {currSession.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>Sessions unavailable</MenuItem>
            )}
          </Select>
        </FormControl>

        <Box
          sx={{
            marginBottom: 2,
            display: "flex",
            gap: "8px",
            marginRight: "auto",
          }}
        >
          <Button
            variant={filter === "All" ? "contained" : "outlined"}
            color="inherit"
            onClick={() => setFilter("All")}
            sx={{
              textTransform: "capitalize",
              borderRadius: "20px",
            }}
          >
            All
          </Button>
          <Button
            variant={filter === "Paid" ? "contained" : "outlined"}
            color="inherit"
            onClick={() => setFilter("Paid")}
            sx={{ textTransform: "capitalize", borderRadius: "20px" }}
          >
            Paid
          </Button>
          <Button
            variant={filter === "Unpaid" ? "contained" : "outlined"}
            color="inherit"
            onClick={() => setFilter("Unpaid")}
            sx={{
              textTransform: "capitalize",
              borderRadius: "20px",
            }}
          >
            Unpaid
          </Button>
        </Box>

        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell className="table-cell">
                        {row?.schoolFee?.feeType?.name}
                      </TableCell>
                      <TableCell className="table-cell">
                        {formatDateOutput(row?.fromDate)}
                      </TableCell>
                      <TableCell className="table-cell">
                        {formatDateOutput(row?.toDate)}
                      </TableCell>
                      <TableCell className="table-cell">
                        {row?.amount.toFixed(2)}
                      </TableCell>
                      <TableCell className="table-cell">
                        {row?.paidAmount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {row?.isDue ? (
                          <Typography
                            sx={{
                              color: "red",
                              fontSize: 16,
                              fontFamily: "Barlow, sans-serif",
                              display: "flex",
                            }}
                          >
                            Not Paid Yet{" "}
                            <Dangerous
                              fontSize="small"
                              color="error"
                              sx={{ marginTop: 0.25, marginLeft: 0.5 }}
                            />
                          </Typography>
                        ) : (
                          formatDateOutput(row?.paidOn)
                        )}
                      </TableCell>
                      <TableCell>
                        {row?.isDue ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{
                              textTransform: "unset",
                              borderRadius: 5,
                            }}
                            onClick={() =>
                              handlePayNowClick(
                                selectedStudentId,
                                row?.id,
                                row?.schoolFee?.grade?.name,
                                row?.schoolFee?.feeType?.name,
                                row?.amount,
                                row?.fromDate,
                                row?.toDate,
                                currentDate,
                                "Cash"
                              )
                            }
                          >
                            Pay Now{" "}
                            <ChevronRight
                              fontSize="small"
                              sx={{ marginLeft: 0.5 }}
                            />
                          </Button>
                        ) : (
                          <Typography
                            sx={{
                              color: "green",
                              fontSize: 16,
                              fontFamily: "Barlow, sans-serif",
                              display: "flex",
                            }}
                          >
                            Paid{" "}
                            <CheckCircle
                              fontSize="small"
                              color="success"
                              sx={{ marginTop: 0.25, marginLeft: 0.5 }}
                            />
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row?.isDue ? (
                          <Button
                            variant="outlined"
                            onClick={() => handleRequestPayment(row?.id)}
                            size="small"
                            sx={{
                              textTransform: "unset",
                              borderRadius: 5,
                            }}
                          >
                            Request Payment{" "}
                            <Payment
                              fontSize="small"
                              sx={{ marginLeft: 0.5 }}
                            />{" "}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            onClick={() => handleDownloadReceipt(row?.id)}
                            size="small"
                            sx={{
                              textTransform: "unset",
                              borderRadius: 5,
                            }}
                          >
                            Download Receipt{" "}
                            <Download
                              fontSize="small"
                              sx={{ marginLeft: 0.5 }}
                            />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={filteredData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            maxWidth: "90%",
            bgcolor: "background.paper",
            borderRadius: 8,
            boxShadow: 24,
            padding: 4,
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ marginBottom: 2, fontWeight: 600, fontSize: 22 }}
          >
            Payment Details
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, marginRight: "20px" }}>
              <Typography sx={{ marginTop: 2 }}>
                <strong>Student Name:</strong> {studentName}
              </Typography>
              <Typography sx={{ marginTop: 2 }}>
                <strong>Fee:</strong> {selectedPayment?.feesName}
              </Typography>
              <Typography sx={{ marginTop: 2, color: "red" }}>
                <strong>Total Amount:</strong>{" "}
                {parseFloat(selectedPayment?.amount).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ marginTop: 2 }}>
                <strong>Grade:</strong> {selectedPayment?.grade}
              </Typography>
              <Typography sx={{ marginTop: 2 }}>
                <strong>From: </strong>{" "}
                {formatDateOutput(selectedPayment?.startDate)}
              </Typography>
              <Typography sx={{ marginTop: 2 }}>
                <strong>To: </strong>{" "}
                {formatDateOutput(selectedPayment?.endDate)}
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            name="paidOn"
            type="date"
            labelid="paidOn"
            label="Paid On"
            value={selectedPayment?.paidOn}
            onChange={handlePaymentDateChange}
            size="small"
            sx={{ marginTop: 3 }}
            required
          />

          <FormControl fullWidth required size="small" sx={{ marginTop: 3 }}>
            <InputLabel id="paymode-label">Payment Mode</InputLabel>
            <Select
              labelId="paymode-label"
              id="paymode"
              value={selectedPayment?.paymentMode}
              label="Payment Mode"
              onChange={handlePaymentModeChange}
              required
              size="small"
            >
              <MenuItem value={"Cash"}>Cash</MenuItem>
              <MenuItem value={"Card"}>Card</MenuItem>
              <MenuItem value={"UPI"}>UPI</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth required size="small" sx={{ marginTop: 3 }}>
            <FormLabel id="demo-row-radio-buttons-group-label">
              Payment Type
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
            >
              <FormControlLabel
                value="Full"
                control={<Radio />}
                label="Full"
                checked={!isPartial}
                onChange={() => setIsPartial(false)}
              />
              <FormControlLabel
                value="partial"
                control={<Radio />}
                label="Partial"
                checked={isPartial}
                onChange={() => setIsPartial(true)}
              />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            name="amount"
            type="number"
            labelid="amount"
            label="Amount"
            value={!isPartial ? selectedPayment?.amount : amountToPay}
            size="small"
            sx={{ marginTop: 3 }}
            required
            disabled={!isPartial}
            inputProps={{
              max: isPartial ? selectedPayment?.amount : undefined,
            }}
            onChange={(event) => setAmountToPay(event.target.value)}
          />

          <Box
            sx={{
              display: "flex",
              marginLeft: "auto",
              marginTop: 5,
              marginBottom: 2,
              gap: 1,
            }}
          >
            <Button
              fullWidth
              onClick={handleCloseModal}
              color="error"
              variant="contained"
              size="medium"
              sx={{ textTransform: "capitalize" }}
            >
              Cancel <Cancel fontSize="small" sx={{ marginLeft: 1 }} />
            </Button>
            <Button
              fullWidth
              onClick={handleConfirmPayment}
              color="success"
              variant="contained"
              size="medium"
              sx={{ textTransform: "capitalize" }}
            >
              Confirm <CheckCircle fontSize="small" sx={{ marginLeft: 1 }} />
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default FeesHistory;
